import { ArrowSquareOut } from "@phosphor-icons/react";
import { useState } from "react";

import { MobileHeading } from "features/layout/components";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import Loader from "common/components/Loader";
import RestrictedComponent from "common/components/RestrictedComponent";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useApiTokens from "../../datahooks/useApiTokens";
import SettingsScrollContent from "../SettingsScrollContent";
import CreateApiTokenButton from "./CreateApiTokenButton";
import DesktopTable from "./DesktopLayout/DesktopTable";
import MasterApiKey from "./MasterApiKey";
import MobileList from "./MobileLayout/MobileList";

import NoAPIKeysFound from "assets/images/empty-placeholders/api-keys.png";
import ResultNotFoundImage from "assets/images/empty-placeholders/result-not-found.png";

export default function ApiKeys() {
  const isDesktop = useTwBreakpoint("lg");
  const { tokens, isLoadingTokens, tokensError, refetchTokens } =
    useApiTokens();
  const [searchTerm, setSearchTerm] = useState("");

  if (tokensError) {
    return (
      <ErrorPlaceholder
        onRetry={refetchTokens}
        errorMessage="Could not display API keys"
      />
    );
  }

  if (isLoadingTokens) {
    return <Loader />;
  }

  const Component = isDesktop ? DesktopTable : MobileList;
  const hasTokens = !!tokens?.length;

  const filteredTokens = (tokens ?? []).filter(({ name }) =>
    name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const actionComponent = (
    <>
      <Button
        variant="secondary-black"
        asChild
        leftIcon={<ArrowSquareOut />}
        size={isDesktop ? "sm" : "md"}
        className="max-lg:order-2 max-lg:ml-auto"
      >
        <a href="https://docs.aimfox.com/" target="_blank" rel="noreferrer">
          Documentation
        </a>
      </Button>

      {hasTokens && <CreateApiTokenButton size={isDesktop ? "sm" : "md"} />}
    </>
  );

  const searchInputComponent = (
    <SearchInput
      placeholder="Search API keys"
      value={searchTerm}
      className="lg:max-w-72"
      variant={isDesktop ? "sm" : "lg"}
      onChange={(e) => setSearchTerm(e.target.value)}
      onClear={() => setSearchTerm("")}
    />
  );

  const renderTokensSection = () => {
    if (!hasTokens) {
      return (
        <EmptyPlaceholder
          title="No API keys found"
          subtitle="Create an API key to connect Aimfox with your application"
          imageSrc={NoAPIKeysFound}
          actionComponent={<CreateApiTokenButton size="md" />}
        />
      );
    }

    if (!filteredTokens.length) {
      return (
        <EmptyPlaceholder
          title="No API keys found"
          subtitle="No API keys match your search criteria. Try adjusting your search"
          imageSrc={ResultNotFoundImage}
          actionComponent={
            <Button onClick={() => setSearchTerm("")}>Clear search</Button>
          }
        />
      );
    }

    return <Component tokens={filteredTokens} />;
  };

  return (
    <SettingsScrollContent desktopActionComponent={actionComponent}>
      <MobileHeading
        actionComponent={actionComponent}
        searchComponent={searchInputComponent}
      />

      {isDesktop && searchInputComponent}

      <RestrictedComponent visibleFor="owner">
        <MasterApiKey />
      </RestrictedComponent>

      {renderTokensSection()}
    </SettingsScrollContent>
  );
}
